import React, { useState, useRef, useEffect, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { moveActor, moveObstacle, pushUndoState, setStageGridSize } from "../../store/sceneSlice";
import { run } from "../../utils/runScript";
import "../../css/Stage.css";
import { findStartBlockIds } from "../../utils/scriptStarts";

// const GRID_WIDTH = 38;
// const GRID_HEIGHT = 22;
const CELL_SIZE = 14;
const DRAG_THRESHOLD = 5;      // px before we call it a drag

const MOVEMENT_DIRECTIONS = {
  right: { name: "Move Right", dx: 1, dy: 0 },
  left: { name: "Move Left", dx: -1, dy: 0 },
  up: { name: "Move Up", dx: 0, dy: -1 },
  down: { name: "Move Down", dx: 0, dy: 1 },
};

const ARROW_KEY_TO_DIRECTION = {
  ArrowRight: "right",
  ArrowLeft: "left",
  ArrowUp: "up",
  ArrowDown: "down",
};

const TOTAL_MOVEMENT_DIRECTIONS = Object.keys(MOVEMENT_DIRECTIONS).length;
const EMPTY_MOVEMENT_MAP = Object.freeze({});
const EMPTY_SCRIPT_LIST = Object.freeze([]);
const INTERACTIVE_TAGS = new Set(["INPUT", "TEXTAREA", "SELECT", "BUTTON"]);

const clampMoveCount = (value) => {
  const num = Number.isFinite(value) ? value : Number(value) || 1;
  return Math.max(1, Math.min(99, num || 1));
};

function collectMovementBlocks(blocks) {
  if (!Array.isArray(blocks) || blocks.length === 0) {
    return EMPTY_MOVEMENT_MAP;
  }

  const found = {};
  let foundCount = 0;

  const visit = (list) => {
    for (const block of list) {
      if (!block) continue;

      for (const [direction, config] of Object.entries(MOVEMENT_DIRECTIONS)) {
        if (!found[direction] && block.name === config.name) {
          found[direction] = clampMoveCount(block.count || 1);
          foundCount += 1;
        }
      }

      if ((block.isContainer || block.type === "repeat") && Array.isArray(block.children) && block.children.length > 0) {
        visit(block.children);
      }

      if (foundCount === TOTAL_MOVEMENT_DIRECTIONS) {
        return;
      }
    }
  };

  visit(blocks);

  if (!foundCount) {
    return EMPTY_MOVEMENT_MAP;
  }

  return found;
}


export default function Stage({
  selectedActorId,
  setSelectedActorId,
  heading,
  showGrid,
}) {

  const containerRef = useRef(null);

  const [gridSize, setGridSize] = useState({
    gridWidth: 0,
    gridHeight: 0,
  });
  const dispatch = useDispatch();
  useEffect(() => {
    function updateGrid() {
      if (containerRef.current) {
        const { clientWidth, clientHeight } = containerRef.current;
        const newGridWidth = Math.max(1, Math.floor(clientWidth / CELL_SIZE));
        const newGridHeight = Math.max(1, Math.floor(clientHeight / CELL_SIZE));

        setGridSize({
          gridWidth: newGridWidth,
          gridHeight: newGridHeight,
        });
        dispatch(setStageGridSize({ gridWidth: newGridWidth, gridHeight: newGridHeight }));
      }
    }

    updateGrid();
    window.addEventListener("resize", updateGrid);
    return () => window.removeEventListener("resize", updateGrid);
  }, [dispatch]);


  // scene/actor state
  const { scenes, currentSceneIndex } = useSelector((s) => s.scene);
  const drawLineSettings = useSelector((s) => s.scene.drawLineSettings || {});
  const scene = scenes[currentSceneIndex];
  const actors = scene?.actors ?? [];
  const obstacles = scene?.obstacles ?? []; // Add obstacles
  const actorTrails = scene?.actorTrails ?? {};
  const selectedActorScripts =
    (actors.find((actor) => actor.id === selectedActorId)?.scripts) || EMPTY_SCRIPT_LIST;
  const movementBlocks = useMemo(
    () => collectMovementBlocks(selectedActorScripts),
    [selectedActorScripts]
  );

  // drag refs/state

  const draggingRef = useRef(false);   // live "am I dragging?"
  const movedRef = useRef(false);   // remembers if threshold crossed

  const [draggedId, setDraggedId] = useState(null);
  const [draggedType, setDraggedType] = useState(null); // 'actor' or 'obstacle'
  const draggedIdRef = useRef(null);
  const draggedTypeRef = useRef(null);
  const [dragStartPos, setDragStart] = useState({ x: 0, y: 0 });
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [dragPosition, setDragPos] = useState({ x: 0, y: 0 });

  const background = scene?.background || "#ffffff";
  const bgStyle = background.startsWith("#")
    ? { backgroundColor: background }
    : {
      backgroundImage: `url(${background})`,
      backgroundSize: "contain",
      backgroundPosition: "center center",
      backgroundRepeat: "no-repeat",
    };

  const getSpeechBubbleStyle = (left, top, size) => {
    const stageEl = containerRef.current;
    const stageWidth = stageEl?.clientWidth || gridSize.gridWidth * CELL_SIZE;
    const stageHeight = stageEl?.clientHeight || gridSize.gridHeight * CELL_SIZE;
    const margin = 12;
    const maxBubbleWidth = Math.min(480, Math.max(160, stageWidth - margin * 2));
    const maxBubbleHeight = Math.min(320, Math.max(140, stageHeight * 0.6));
    const clamp = (value, min, max) => Math.max(min, Math.min(max, value));
    const half = maxBubbleWidth / 2;
    const minX = half + margin;
    const maxX = stageWidth - half - margin;
    const anchorX = stageWidth && minX <= maxX ? clamp(left, minX, maxX) : left;
    const desiredY = top - size * 0.6;
    const minY = maxBubbleHeight + margin;
    const maxY = stageHeight - margin;
    const anchorY = stageHeight && minY <= maxY ? clamp(desiredY, minY, maxY) : desiredY;
    return {
      left: anchorX,
      top: anchorY,
      transform: "translate(-50%, -100%)",
      maxWidth: maxBubbleWidth,
      maxHeight: maxBubbleHeight,
    };
  };

  const renderSpeechContent = (actor) => {
    const message = actor?.speechMessage;
    if (typeof message !== "string") return message;
    const highlightWords = Array.isArray(actor?.aiHighlightWords) ? actor.aiHighlightWords : [];
    const shouldHighlight = highlightWords.length > 0 && actor?.aiLastResponse === message;
    if (!shouldHighlight) return message;

    const highlightSet = new Set(highlightWords.map((w) => String(w || "").toLowerCase()));
    const parts = message.split(/(\b[\w']+\b)/);

    return parts.map((part, idx) => {
      if (!part) return null;
      const normalized = part.toLowerCase();
      if (highlightSet.has(normalized)) {
        return (
          <span key={`hl-${idx}`} className="speech-highlight">
            {part}
          </span>
        );
      }
      return <span key={`tx-${idx}`}>{part}</span>;
    });
  };

  // Notify simulator when the background changes — required for line follower
  useEffect(() => {
    window.dispatchEvent(new Event("simulatorBackgroundChanged"));
  }, [background]);

  useEffect(() => {
    if (!selectedActorId) return;
    if (movementBlocks === EMPTY_MOVEMENT_MAP) return;

    const handleKeyDown = (event) => {
      const activeElement = event.target;
      if (activeElement) {
        const tag = activeElement.tagName?.toUpperCase();
        if ((tag && INTERACTIVE_TAGS.has(tag)) || activeElement.isContentEditable) {
          return;
        }
      }

      const directionKey = ARROW_KEY_TO_DIRECTION[event.key];
      if (!directionKey) return;

      const steps = movementBlocks[directionKey];
      if (!steps) return;

      const vector = MOVEMENT_DIRECTIONS[directionKey];
      if (!vector) return;

      event.preventDefault();
      dispatch(moveActor({
        actorId: selectedActorId,
        dx: vector.dx * steps,
        dy: vector.dy * steps,
        fromScript: true,
      }));
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [dispatch, movementBlocks, selectedActorId]);

  function clamp(v, min, max) {
    return Math.max(min, Math.min(max, v));
  }

  function stageCoords(clientX, clientY) {
    const rect = containerRef.current.getBoundingClientRect();
    const left = clamp(
      clientX - rect.left - dragOffset.x,
      CELL_SIZE / 2,
      (gridSize.gridWidth - 0.5) * CELL_SIZE
    );
    const top = clamp(
      clientY - rect.top - dragOffset.y,
      CELL_SIZE / 2,
      (gridSize.gridHeight - 0.5) * CELL_SIZE
    );
    return { x: left, y: top };
  }

  /* -------- drag handlers -------- */
  function onDragStart(e, item, type) {
    e.preventDefault();
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const itemLeft = (item.x + 0.5) * CELL_SIZE;
    const itemTop = (item.y + 0.5) * CELL_SIZE;
    const clientX = e.clientX ?? e.touches[0].clientX;
    const clientY = e.clientY ?? e.touches[0].clientY;
    setDraggedId(item.id);
    setDraggedType(type);
    draggedIdRef.current = item.id;
    draggedTypeRef.current = type;
    if (type === 'actor') {
      setSelectedActorId(item.id);
    }
    setDragOffset({ x: clientX - rect.left - itemLeft, y: clientY - rect.top - itemTop });
    setDragPos({ x: itemLeft, y: itemTop });
    setDragStart({ x: clientX, y: clientY });
    draggingRef.current = false;
    movedRef.current = false;
    window.addEventListener("mousemove", onDragMove);
    window.addEventListener("mouseup", onDragEnd);
    window.addEventListener("touchmove", onDragMove, { passive: false });
    window.addEventListener("touchend", onDragEnd);
    window.addEventListener("touchcancel", onDragEnd);
  }

  function onDragMove(e) {
    if (!draggedIdRef.current) return;
    const clientX = e.clientX ?? e.touches[0].clientX;
    const clientY = e.clientY ?? e.touches[0].clientY;
    const dx = Math.abs(clientX - dragStartPos.x);
    const dy = Math.abs(clientY - dragStartPos.y);
    if (!draggingRef.current && (dx > DRAG_THRESHOLD || dy > DRAG_THRESHOLD)) {
      draggingRef.current = true;
      movedRef.current = true;
    }
    if (!draggingRef.current) return;
    e.preventDefault();
    setDragPos(stageCoords(clientX, clientY));
  }

  function onDragEnd(e) {
    e.preventDefault();
    if (!draggedIdRef.current) return;
    if (draggingRef.current) {
      const clientX = e.clientX ?? e.changedTouches?.[0]?.clientX;
      const clientY = e.clientY ?? e.changedTouches?.[0]?.clientY;
      const pos = stageCoords(clientX, clientY);
      const gridX = Math.round(pos.x / CELL_SIZE - 0.5);
      const gridY = Math.round(pos.y / CELL_SIZE - 0.5);

      if (draggedTypeRef.current === 'actor') {
        const actor = actors.find((a) => a.id === draggedIdRef.current);
        if (actor && (gridX !== actor.x || gridY !== actor.y)) {
          dispatch(pushUndoState());
          dispatch(moveActor({ actorId: actor.id, dx: gridX - actor.x, dy: gridY - actor.y }));
        }
      } else if (draggedTypeRef.current === 'obstacle') {
        const obstacle = obstacles.find((o) => o.id === draggedIdRef.current);
        if (obstacle && (gridX !== obstacle.x || gridY !== obstacle.y)) {
          dispatch(pushUndoState());
          dispatch(moveObstacle({ obstacleId: obstacle.id, dx: gridX - obstacle.x, dy: gridY - obstacle.y }));
        }
      }
    }
    draggingRef.current = false;
    setDraggedId(null);
    setDraggedType(null);
    draggedIdRef.current = null;
    draggedTypeRef.current = null;
    setDragOffset({ x: 0, y: 0 });
    setDragPos({ x: 0, y: 0 });
    window.removeEventListener("mousemove", onDragMove);
    window.removeEventListener("mouseup", onDragEnd);
    window.removeEventListener("touchmove", onDragMove);
    window.removeEventListener("touchend", onDragEnd);
    window.removeEventListener("touchcancel", onDragEnd);
  }

  /* -------- tap handler -------- */
    const handleActorTap = (actor, e) => {
        e.stopPropagation();
        if (movedRef.current) {
            movedRef.current = false;
            return;
        }
        setSelectedActorId(actor.id);
        const tapStackIds = findStartBlockIds(actor.scripts, (b) => b.name === "Start On Tap");
        if (tapStackIds.length) {
            run(actor, dispatch, scene?.sounds, actor.id, { startBlockIds: tapStackIds }).catch((err) =>
                console.error("tap-script error:", err)
            );
        }
  };

  /* -------- obstacle renderer -------- */
  const renderObstacle = (obstacle) => {
    const isDragged = draggedId === obstacle.id && draggedType === 'obstacle' && draggingRef.current;
    const left = isDragged ? dragPosition.x : (obstacle.x + 0.5) * CELL_SIZE;
    const top = isDragged ? dragPosition.y : (obstacle.y + 0.5) * CELL_SIZE;
    const size = CELL_SIZE * 2; // Obstacles are 2 cells in size

    let obstacleElement;
    switch (obstacle.shape) {
      case 'square':
        obstacleElement = (
          <div
            style={{
              width: size,
              height: size,
              backgroundColor: '#4a90e2',
              borderRadius: '4px',
            }}
          />
        );
        break;
      case 'triangle':
        obstacleElement = (
          <div
            style={{
              width: 0,
              height: 0,
              borderLeft: `${size / 2}px solid transparent`,
              borderRight: `${size / 2}px solid transparent`,
              borderBottom: `${size}px solid #28a745`,
            }}
          />
        );
        break;
      case 'circle':
        obstacleElement = (
          <div
            style={{
              width: size,
              height: size,
              backgroundColor: '#ffc107',
              borderRadius: '50%',
            }}
          />
        );
        break;
      default:
        obstacleElement = (
          <div
            style={{
              width: size,
              height: size,
              backgroundColor: '#6c757d',
              borderRadius: '4px',
            }}
          />
        );
    }

    return (
      <div
        key={obstacle.id}
        className="obstacle"
        onMouseDown={(e) => onDragStart(e, obstacle, 'obstacle')}
        onTouchStart={(e) => onDragStart(e, obstacle, 'obstacle')}
        style={{
          position: 'absolute',
          left,
          top,
          transform: 'translate(-50%, -50%)',
          cursor: 'grab',
          transition: isDragged ? 'none' : 'left 0.1s linear, top 0.1s linear',
          zIndex: 2,
          userSelect: 'none',
        }}
      >
        {obstacleElement}
      </div>
    );
  };

  /* -------- render -------- */
  const renderActorTrails = () => {
    if (!drawLineSettings?.enabled) return null;
    const entries = Object.entries(actorTrails);
    if (!entries.length) return null;

    const pathElements = entries
      .filter(([actorId]) => actors.some(a => a.id === actorId))
      .map(([actorId, points]) => {
        if (!points || points.length < 2) return null;
        let d = "";
        points.forEach((point, index) => {
          const px = (point.x + 0.5) * CELL_SIZE;
          const py = (point.y + 0.5) * CELL_SIZE;
          d += `${index === 0 ? "M" : " L"} ${px} ${py}`;
        });
        return (
          <path
            key={`trail-${actorId}`}
            d={d}
            stroke={drawLineSettings.color || "#FF0000"}
            strokeWidth={drawLineSettings.thickness || 3}
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
            opacity={0.85}
          />
        );
      });

    return (
      <svg
        className="stage-trails"
        width="100%"
        height="100%"
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          pointerEvents: "none",
          zIndex: 1,
        }}
      >
        {pathElements}
      </svg>
    );
  };

  return (
    <div
      ref={containerRef}
      id="stage-area"
      style={{
        ...bgStyle,
        position: "relative",
        width: "100%",
        height: "100%",
        margin: "auto",
        userSelect: "none",
        overflow: "hidden",
      }}
    >
      {showGrid && <GridOverlay cols={gridSize.gridWidth} rows={gridSize.gridHeight} cellSize={CELL_SIZE} />}
      {renderActorTrails()}

      {heading?.text && (
        <div
          style={{
            position: "absolute",
            width: "100%",
            top: 8,
            left: 0,
            textAlign: "center",
            color: heading.color,
            fontSize: heading.size,
            fontWeight: "bold",
            pointerEvents: "none",
            textShadow:
              heading.color === "#fff"
                ? "0 2px 7px #20398880"
                : "0 1px 3px #ffffff70",
            zIndex: 8,
          }}
        >
          {heading.text}
        </div>
      )}

      {/* Render obstacles */}
      {obstacles.map(renderObstacle)}

      {/* Render actors */}
      {actors.map((actor) => {
        if (actor.visible === false) return null;
        const isDragged = draggedId === actor.id && draggedType === 'actor' && draggingRef.current;
        const left = isDragged ? dragPosition.x : (actor.x + 0.5) * CELL_SIZE;
        const top = isDragged ? dragPosition.y : (actor.y + 0.5) * CELL_SIZE;
        const size = CELL_SIZE * 4 * (actor.size || 1);
        const rotation = actor.direction || 0;
        const isSelected = actor.id === selectedActorId;
        const shouldAnimate = !actor.disableTransition && !(actor.lastMoveWrapped && actor.lastMoveWrapped === true);
        return (
          <React.Fragment key={actor.id}>
            <img
              src={actor.image}
              alt={actor.name}
              crossOrigin="anonymous"
              draggable={false}
              className={`actor${isSelected ? " selected" : ""}`}
              onMouseDown={(e) => onDragStart(e, actor, 'actor')}
              onTouchStart={(e) => onDragStart(e, actor, 'actor')}
              onClick={(e) => handleActorTap(actor, e)}
              style={{
                position: "absolute",
                width: size,
                height: size,
                left,
                top,
                transform: `translate(-50%, -50%) rotate(${actor.direction || 0}deg)`,
                cursor: "grab",
                transition: isDragged || !shouldAnimate ? "none" : "left 0.1s linear, top 0.1s linear",
                zIndex: isSelected ? 10 : 1,
                userSelect: "none",
              }}
            />

            {/* Speech bubble */}
            {actor.speechMessage && (
              <div
                className="speech-bubble"
                style={getSpeechBubbleStyle(left, top, size)}
              >
                {renderSpeechContent(actor)}
              </div>
            )}
          </React.Fragment>
        );

      })}
    </div>
  );
}

function GridOverlay({ cols, rows, cellSize }) {
  return (
    <svg
      className="stage-grid"
      width="100%"
      height="100%"
      style={{ position: "absolute", top: 0, left: 0, pointerEvents: "none", zIndex: 5 }}
    >
      {[...Array(cols + 1)].map((_, i) => (
        <line
          key={`v${i}`}
          x1={`${(i * 100) / cols}%`}
          y1="0"
          x2={`${(i * 100) / cols}%`}
          y2="100%"
          stroke="#bbb"
          strokeWidth="1"
        />
      ))}
      {[...Array(rows + 1)].map((_, i) => (
        <line
          key={`h${i}`}
          x1="0"
          y1={`${(i * 100) / rows}%`}
          x2="100%"
          y2={`${(i * 100) / rows}%`}
          stroke="#bbb"
          strokeWidth="1"
        />
      ))}
    </svg>
  );
}
